import { useState, useEffect, useCallback } from "react";
import {
  useAuthInfo,
  authErrorRedirect,
  apiURI,
  useFormDropdownFields,
  downloadCsvViaExport,
  buildCsvColumns,
} from "../../common";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  DelegationListTable,
  COLUMN_CONFIG,
} from "./DelegationListTable.component";
import { UpdateApproverModal } from "./UpdateApproverModal.component";
import { UpdateApproverButton } from "./UpdateApproverButton.component";
import { SkippedRequestsModal } from "./SkippedRequestsModal.component";
import { validateChefEmailInput } from "../validation/validateChefEmailInput";

export function DelegationItems() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useNavigate();
  const decoded = useAuthInfo();
  const [errors, setErrors] = useState();
  const { executiveChefList = [], hodList = [] } = useFormDropdownFields();
  const [approverType, setApproverType] = useState(
    decoded
      ? decoded.role === "procurement"
        ? "chef"
        : "executiveChef"
      : "executiveChef"
  );
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [itemList, setItemList] = useState();
  const [updateDisabled, setUpdateDisabled] = useState(true);
  // Server-side pagination: the grid asks for a page, the backend returns just
  // that page plus the total row count.
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);
  // Per-column server-side search: { [field]: value }. `value` is a string for
  // text/status columns and { from, to } for date columns. Empty entries are
  // removed so the query param only carries active filters.
  const [searchModel, setSearchModel] = useState({});
  const searchKey = JSON.stringify(searchModel);
  // Bumped whenever the column inputs should visually reset (clear-all, or an
  // approver-type switch) — the table keys its header controls on this.
  const [filterResetKey, setFilterResetKey] = useState(0);
  const activeFilterCount = Object.keys(searchModel).length;

  // Clears every active column search at once.
  const handleClearAllSearch = useCallback(() => {
    setSearchModel({});
    setFilterResetKey((k) => k + 1);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Server-side single-column sort: [] or [{ field, sort: "asc" | "desc" }].
  const [sortModel, setSortModel] = useState([]);
  const sortKey = JSON.stringify(sortModel);

  // Cycles a column's sort: none → asc → desc → none, and restarts paging.
  const handleSortChange = useCallback((field) => {
    setSortModel((prev) => {
      const current = prev[0];
      if (!current || current.field !== field) return [{ field, sort: "asc" }];
      if (current.sort === "asc") return [{ field, sort: "desc" }];
      return [];
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  // Applies a single column's search value, dropping it when cleared, and
  // restarts paging from the first page so results aren't shown on a stale page.
  const handleSearchChange = useCallback((field, value) => {
    setSearchModel((prev) => {
      const next = { ...prev };
      const isEmpty =
        value == null ||
        value === "" ||
        (typeof value === "object" && !value.from && !value.to);
      if (isEmpty) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, []);

  //state and handlers for chef textfield select
  const [chefEmailTextfield, setEmailTextfield] = useState("");
  const handleChefEmailTextField = (e) => {
    setEmailTextfield(e.target.value);
  };
  //state and handlers for executive chef dropdown select
  const [executiveChefEmailSelect, setExecutiveChefEmailSelect] = useState("");
  const handleExecChefEmailSelect = (value) => {
    setExecutiveChefEmailSelect(value ? value.email : "");
  };
  //state and handlers for HOD dropdown select
  const [HODDepartmentSelect, setHODDepartmentSelect] = useState("");
  const handleHODDepartmentSelect = (value) => {
    setHODDepartmentSelect(value ? value.department : "");
  };

  const [openUpdateApproverModal, setOpenUpdateApproverModal] = useState(false);
  const handleOpenUpdateApproverModal = () => {
    setOpenUpdateApproverModal(true);
  };

  // Requests the backend refused to update (e.g. exec-chef-raised rows on chef
  // delegation, or inconsistent data). Shown in a modal after submit.
  const [skippedRows, setSkippedRows] = useState([]);
  const [openSkippedModal, setOpenSkippedModal] = useState(false);
  const handleCloseSkippedModal = () => setOpenSkippedModal(false);

  // Enriches each skipped {requestID, reason} from the API with the identifying
  // details of the matching row, resolved from the currently loaded page before
  // it is cleared by the reload.
  function buildSkippedRows(skipped, rows) {
    return (skipped ?? []).map(({ requestID, reason }) => {
      const match = rows?.find((row) => row.sampleRequestID === requestID);
      return {
        sampleRequestID: requestID,
        email: match?.email,
        executiveChefEmail: match?.executiveChefEmail,
        reason,
      };
    });
  }

  // Clears the current grid selection (both the model driving the checkboxes
  // and the derived request-ID list).
  function clearSelection() {
    setRowSelectionModel([]);
    setSelectedRowsData([]);
    setUpdateDisabled(true);
  }

  // Switching approver type restarts paging from the first page and clears any
  // active column search (the columns/base filter differ per approver type).
  function handleApproverTypeChange(value) {
    setApproverType(value);
    setSearchModel({});
    setSortModel([]);
    setFilterResetKey((k) => k + 1);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }

  function onRowsSelectionHandler(ids) {
    setRowSelectionModel(ids ?? []);
    const selectedRows = (ids ?? [])
      .map((id) => itemList?.find((row) => row.id === id)?.sampleRequestID)
      .filter(Boolean);
    setSelectedRowsData(selectedRows);
    setUpdateDisabled(selectedRows.length === 0);
  }

  const handleClose = () => {
    setOpenUpdateApproverModal(false);
    setEmailTextfield("");
    setExecutiveChefEmailSelect("");
    setHODDepartmentSelect("");
  };

  async function getData(requestURN) {
    try {
      const hasSearch = Object.keys(searchModel).length > 0;
      const hasSort = sortModel.length > 0;
      const response = await axios.get(`${apiURI}/form/${requestURN}`, {
        params: {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          // Only send the search param when there are active filters.
          ...(hasSearch ? { search: JSON.stringify(searchModel) } : {}),
          // Only send the sort param when a column sort is active.
          ...(hasSort
            ? {
                sort: JSON.stringify({
                  field: sortModel[0].field,
                  dir: sortModel[0].sort,
                }),
              }
            : {}),
        },
      });
      const { rows = [], total = 0 } = response.data ?? {};
      // Composite id keeps each flattened item row unique within the page.
      setItemList(
        rows.map((row) => ({
          ...row,
          id: `${row.sampleRequestID}__${row.sampleItemID}`,
        }))
      );
      setRowCount(total);
    } catch (error) {
      setItemList([]);
      setRowCount(0);
      setErrors(error?.response?.data);
      authErrorRedirect(error?.response?.data, history);
    }
  }

  // Resolves the approver-list endpoint (after `${apiURI}/form/`) for the
  // current role + approver type. Shared by the paginated load and the CSV
  // export so both hit the same list.
  function resolveEndpoint() {
    if (!decoded) return undefined;
    if (decoded.role === "procurement") {
      switch (approverType) {
        case "chef":
          return "procChefApproverList";
        case "executiveChef":
          return "procExecChefApproverList";
        case "hod":
          return "procHODApproverList";
        default:
          return undefined;
      }
    }
    switch (approverType) {
      case "executiveChef":
        return `execChefApproverList/${decoded.email}`;
      case "hod":
        return `HODApproverList/${decoded.email}`;
      default:
        return undefined;
    }
  }

  // Loads the correct approver list page for the current role + approver type.
  function loadData() {
    setItemList();
    const endpoint = resolveEndpoint();
    if (endpoint) getData(endpoint);
  }

  // Downloads the FULL result set for the current approver type + search + sort
  // as a CSV (built + streamed by the backend), reusing the same endpoint.
  function handleExport() {
    // resolveEndpoint() is null for an invalid role/approverType combo — bail
    // rather than fire a request the server would 403.
    if (!resolveEndpoint()) return Promise.resolve();
    return downloadCsvViaExport({
      list: "delegate",
      // Non-identifying selector; the server derives identity scope from req.user.
      approverType,
      columns: buildCsvColumns(COLUMN_CONFIG),
      search: searchModel,
      sort: sortModel.length
        ? { field: sortModel[0].field, dir: sortModel[0].sort }
        : undefined,
      filename: `delegate-${approverType}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
    });
  }

  useEffect(() => {
    // Any list/page/search change invalidates the prior selection.
    clearSelection();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    approverType,
    paginationModel.page,
    paginationModel.pageSize,
    searchKey,
    sortKey,
  ]);

  async function handleSubmit(event) {
    event.preventDefault();
    setErrors({});

    // Resolve + validate the approver for the active approver type.
    let approver;
    switch (approverType) {
      case "hod":
        if (!HODDepartmentSelect) {
          setErrors({ HODDepartment: "HOD department is required" });
          return;
        }
        approver = HODDepartmentSelect;
        break;
      case "executiveChef":
        if (!executiveChefEmailSelect) {
          setErrors({ executiveChefEmail: "Executive chef email is required" });
          return;
        }
        approver = executiveChefEmailSelect;
        break;
      case "chef": {
        const { validationErrors, isValid } = validateChefEmailInput(
          chefEmailTextfield,
          executiveChefList
        );
        if (!isValid) {
          setErrors(validationErrors);
          return;
        }
        approver = chefEmailTextfield;
        break;
      }
      default:
        return;
    }

    if (!approver) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${apiURI}/form/delegateApprover`, {
        selectedRowsData: selectedRowsData,
        approverEmail: approver,
        approverType: approverType,
      });
      // Resolve skipped-row details from the current page BEFORE it is cleared
      // by the reload below, then surface them in a modal.
      const skipped = buildSkippedRows(response?.data?.skipped, itemList);
      handleClose();
      clearSelection();
      // Re-fetch the current page instead of a full page reload.
      loadData();
      if (skipped.length) {
        setSkippedRows(skipped);
        setOpenSkippedModal(true);
      }
    } catch (error) {
      setErrors(
        error?.response?.data ?? { general: "Failed to delegate approver." }
      );
      authErrorRedirect(error?.response?.data, history);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <DelegationListTable
        title="Delegate Item Approval"
        description={`Items you have approved will be displayed here. `}
        data={itemList}
        onRowsSelectionHandler={onRowsSelectionHandler}
        rowSelectionModel={rowSelectionModel}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={rowCount}
        button={
          <UpdateApproverButton
            handleOpen={handleOpenUpdateApproverModal}
            updateDisabled={updateDisabled}
          />
        }
        approverType={approverType}
        setApproverType={handleApproverTypeChange}
        decoded={decoded}
        searchModel={searchModel}
        onSearchChange={handleSearchChange}
        onClearAllSearch={handleClearAllSearch}
        activeFilterCount={activeFilterCount}
        filterResetKey={filterResetKey}
        sortModel={sortModel}
        onSortChange={handleSortChange}
        onExport={handleExport}
      />

      {selectedRowsData && (
        <UpdateApproverModal
          open={openUpdateApproverModal}
          setOpen={setOpenUpdateApproverModal}
          handleOpen={handleOpenUpdateApproverModal}
          itemList={itemList}
          approverType={approverType}
          selectedRows={selectedRowsData}
          chefEmailTextfield={chefEmailTextfield}
          handleChefEmailTextField={handleChefEmailTextField}
          executiveChefEmailSelect={executiveChefEmailSelect}
          handleExecChefEmailSelect={handleExecChefEmailSelect}
          setExecutiveChefEmailSelect={setExecutiveChefEmailSelect}
          setHODDepartmentSelect={setHODDepartmentSelect}
          HODDepartmentSelect={HODDepartmentSelect}
          handleHODDepartmentSelect={handleHODDepartmentSelect}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          errors={errors}
          executiveChefList={executiveChefList}
          hodList={hodList}
          isSubmitting={isSubmitting}
        />
      )}

      <SkippedRequestsModal
        open={openSkippedModal}
        onClose={handleCloseSkippedModal}
        skippedRows={skippedRows}
      />
    </>
  );
}
